<template>
  <div class="press-release">
    <div class="page-wrapper" v-if="entry">
      <header class="press-release__header c_wrapper c_wrapper--950">
        <Button
          v-if="entry.pressReleaseCategory && entry.pressReleaseCategory.length"
          class="press-release__header__breadcrumb"
          unstyle
          reset
          :href="entry.pressReleaseCategory[0].uri"
          :label-text="entry.pressReleaseCategory[0].title"
        />
        <h1 class="press-release__header__header c_header--6 c_text--graphik_bold">{{ entry.title }}</h1>
        <div class="press-release__header__summary" v-if="entry.articleSummary" v-html="entry.articleSummary"></div>
        <div class="press-release__header__location_date">
          <p class="press-release__header__location_date__location" v-if="entry.location">{{ entry.location }}</p>
          <p v-if="formattedPostDate">{{ formattedPostDate }}</p>
        </div>
        <div class="press-release__action_link_container">
          <div class="press-release__action_link_item" v-if="entry.pressKit[0]">
            <div class="press-release__press_kit">
              <a :href="entry.pressKit[0].url" class="press-release__press_kit_button">
                <span class="press-release__press_kit__button__text c_text--graphik_semibold">{{
                  $t('Press Kit')
                }}</span>
                <IconSVG
                  width="15"
                  height="15"
                  handle="file_download"
                  aria-hidden="true"
                  class="icon"
                  style="padding-left: 5px"
                />
              </a>
            </div>
          </div>
          <div class="press-release__action_link_item">
            <SocialShare
              class="press-release__social_share"
              :share-title="seomatic.title"
              :share-text="getSeomaticDescription ? getSeomaticDescription : ''"
              :share-url="entry.uri"
              v-if="seomatic.title && entry.uri"
            >
              <slot>
                <div class="press-release__social_share__button">
                  <span class="press-release__social_share__button__text c_text--graphik_semibold">{{
                    $t('Share')
                  }}</span>
                  <IconSVG width="15" height="15" handle="new_window" aria-hidden="true" />
                </div>
              </slot>
              <div slot="fallback">
                <CopyToClipboard
                  class="press-release__social_share__copy_to_clipboard"
                  copy-label="Copy URL to clipboard"
                  :copy-text="getFullUrl"
                />
              </div>
            </SocialShare>
          </div>
        </div>
      </header>
      <div class="c_wrapper c_wrapper--950 press-release__matrix-container">
        <MatrixBlockBlogPageContent :matrix-blocks="entry.blogContent" v-if="entry.blogContent" />
      </div>
      <div class="c_wrapper c_wrapper--950 press-release__attachments" v-if="entry.articleAttachments[0]">
        <h1 class="press-release__header__header c_header--4 c_text--graphik_bold">{{ $t('Attachments') }}</h1>
        <ul v-for="(attachment, index) in entry.articleAttachments" :key="index">
          <li>
            <a :href="attachment.url">{{ attachment.title }}</a>
          </li>
        </ul>
      </div>
      <MatrixBlockLandingPageContent
        class="press-release__lp_matrix"
        :matrix-blocks="entry.landingPageContent"
        :related-posts-ids="relatedPostsIds"
        :entry-id="entry.id"
        v-if="entry.landingPageContent"
      />
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  reactive,
  toRefs,
  useContext,
  useFetch,
  useMeta,
  useRoute,
  useStore,
} from '@nuxtjs/composition-api';
import { pressReleaseGql } from 'GQL/pages/pressReleaseGql.js';
import { gqlToObject } from 'JS/seomatic.js';
import { getFullUrl, log, selectedCategoriesFromCategoriesField, translatePostDate } from 'JS/global.js';
import Button from 'Components/button/Button.vue';
import CopyToClipboard from 'Components/copy_to_clipboard/CopyToClipboard.vue';
import IconSVG from 'Components/icon_svg/IconSVG.vue';
import MatrixBlockBlogPageContent from 'Components/matrix_blocks/MatrixBlockBlogPageContent.vue';
import MatrixBlockLandingPageContent from 'Components/matrix_blocks/MatrixBlockLandingPageContent.vue';
import SocialShare from 'Components/social_share/SocialShare.vue';

export default defineComponent({
  name: 'PagePressRelease',
  head: {},
  components: {
    Button,
    CopyToClipboard,
    IconSVG,
    MatrixBlockBlogPageContent,
    MatrixBlockLandingPageContent,
    SocialShare,
  },
  setup() {
    const { app, $craft, payload, $preview } = useContext();
    const route = useRoute();
    const store = useStore();
    const state = reactive({
      entry: null,
      relatedPostsIds: [],
      seomatic: null,
    });

    useFetch(async () => {
      let data;
      if (!$preview && payload?.entry) {
        data = payload;
      } else {
        const request = await $craft({
          apiLog: 'PagePressRelease',
          query: pressReleaseGql,
          variables: {
            site: store.state.global.localeSites[app.i18n.locale].siteHandle,
            uri: `${route.value.params.contentSection + '/press-release/' + route.value.params.slug}`,
          },
        });

        if (request?.data) {
          data = request.data;
        }
      }

      if (data?.entry) {
        state.entry = data.entry;

        if (data.entry.relatedProductCategories) {
          state.relatedPostsIds = selectedCategoriesFromCategoriesField(data.entry.relatedProductCategories);
        }
        if (data.entry.seomatic) {
          state.seomatic = data.entry.seomatic;
        }
        if (state.entry?.navigationTheme) {
          store.commit('global/updateGlobalDarkTheme', true);
        } else {
          store.commit('global/updateGlobalDarkTheme', false);
        }
      }
    });

    useMeta(() => {
      return state.seomatic ? { ...gqlToObject(state.seomatic) } : {};
    });

    return { ...toRefs(state) };
  },
  computed: {
    formattedPostDate() {
      return translatePostDate(this.entry?.entryPostDate, this.$i18n?.locale);
    },
    getSeomaticDescription() {
      return this.seomatic?.meta
        ?.filter((obj) => {
          return obj.name === 'description';
        })
        .map((descriptionObj) => {
          return descriptionObj.content;
        })[0];
    },
    getFullUrl,
  },
  mounted() {
    log('PagePressRelease loaded');
  },
});
</script>

<style lang="scss">
.press-release {
  $self: &;
  @at-root #{$self}__header {
    padding-top: px_to_rem(25);
    padding-bottom: px_to_rem(25);
    @include mq($mq_sm_to_md) {
      padding-top: px_to_rem(45);
      padding-bottom: px_to_rem(45);
    }
    @at-root #{&}__breadcrumb {
      display: inline-block;
      color: $color_gray_112;
      margin-bottom: px_to_rem(25);
      &:hover,
      &:focus {
        text-decoration: none;
        color: $color_black;
      }
    }
    @at-root #{&}__header {
      line-height: 1.2;
      padding-bottom: px_to_rem(25);
    }
    @at-root #{&}__summary {
      padding-bottom: px_to_rem(30);
    }
    @at-root #{$self}__matrix-container {
      padding-bottom: 50px;
    }
    @at-root #{&}__location_date {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: space-between;
      color: $color_gray_112;
      @include mq(500) {
        justify-content: flex-start;
      }
      @at-root #{&}__location {
        padding-right: px_to_rem(40);
      }
    }
  }
  @at-root #{$self}__action_link_container {
    @at-root #{$self}__action_link_item {
      display: inline-block;
      padding-right: 20px;
    }
    @at-root #{$self}__action_link_item:last-child {
      padding-right: 0px;
    }
  }
  @at-root #{$self}__press_kit {
    padding-top: px_to_rem(25);
    @at-root #{&}_button {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      color: #000;
      text-decoration: none;
      &:hover,
      &:focus {
        color: $color_red;
      }
      @at-root #{&}__text {
        padding-right: px_to_rem(5);
      }
    }
  }
  @at-root #{$self}__social_share {
    padding-top: px_to_rem(25);
    @at-root #{&}__button {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      &:hover,
      &:focus {
        color: $color_red;
      }
      @at-root #{&}__text {
        padding-right: px_to_rem(5);
      }
    }
    @at-root #{&}__copy_to_clipboard {
      display: grid;
      grid-template-columns: 1fr px_to_rem(100);
      grid-gap: px_to_rem(5) px_to_rem(16);
      max-width: px_to_rem(400);
      font-size: px_to_rem(14);
      .c_copy_to_clipboard {
        @at-root #{&}__label {
          grid-column: 1/3;
          grid-row: 1;
        }
        @at-root #{&}__input {
          grid-column: 1;
          grid-row: 2;
        }
        @at-root #{&}__button {
          grid-column: 2;
          grid-row: 2;
        }
      }
    }
  }
  @at-root #{$self}__lp_matrix {
    padding-top: px_to_rem(50);
  }
  @at-root #{$self}__attachments {
    padding-bottom: px_to_rem(50);
    a {
      color: $color_red;
      &:hover,
      &:focus {
        color: $color_red;
      }
    }
  }
}
</style>
